import { logger } from '~entities/logger';

import { isAuthError } from '~shared/api/errors';
import type { RequestParams, ScpClient } from '~shared/api/scp/client';

import type { UserInfo } from '../../auth';
import type {
  AuthApi,
  AuthSuccessResponse,
  InitiateParams,
  RespondParams,
  ValidateResponse,
} from '../auth-api';
import { ChallengeConfirmationError } from '../errors';

interface ConstructorParams {
  client: ScpClient;
  clientId: string;
  host: string;
}

export interface GatewayOtpResponse {
  session: string;
  challengeKind: string;
  challengeParameters: {
    username: string;
    delaySec: string;
    expiresAt: string;
    isNew: boolean;
  };
}

function isGatewayOtpResponse(
  response: GatewayOtpResponse | AuthSuccessResponse
): response is GatewayOtpResponse {
  return typeof (response as GatewayOtpResponse)?.session === 'string';
}

export class GatewayAuthApi implements AuthApi {
  private client: ScpClient;
  private clientId: string;
  private host: string;

  private session: string | null = null;
  private username: string | null = null;

  constructor(params: ConstructorParams) {
    this.clientId = params.clientId;
    this.client = params.client;
    this.host = params.host;
  }

  async initiate(params: InitiateParams) {
    this.session = null;
    this.username = null;

    const response = await this.makeRequest<GatewayOtpResponse>({
      method: 'initiate',
      payload: {
        clientId: this.clientId,
        authParameters: { phoneNumber: params.phoneNumber },
      },
    }).then((resp) => resp.payload);

    this.session = response.session;
    this.username = response.challengeParameters.username;

    return response;
  }

  async respond(params: RespondParams): Promise<AuthSuccessResponse> {
    this.ensureSession();

    const response = await this.makeRequest<
      GatewayOtpResponse | AuthSuccessResponse
    >({
      method: 'respond',
      payload: {
        clientId: this.clientId,
        session: this.session,
        challengeResponses: {
          answer: params.answer,
          username: this.username,
        },
      },
    }).then((resp) => resp.payload);

    if (isGatewayOtpResponse(response)) {
      this.session = response.session;
      this.username = response.challengeParameters.username;
      throw new ChallengeConfirmationError('Wrong code');
    }

    this.session = null;
    this.username = null;

    return response;
  }

  async resendConfirmation() {
    this.ensureSession();

    const response = await this.makeRequest<GatewayOtpResponse>({
      method: 'respond',
      payload: {
        clientId: this.clientId,
        session: this.session,
        challengeResponses: {
          repeatChallenge: true,
          username: this.username,
        },
      },
    }).then((resp) => resp.payload);

    this.session = response.session;
    this.username = response.challengeParameters.username;

    return response;
  }

  async validate(): Promise<ValidateResponse> {
    return this.makeRequest<ValidateResponse>({
      service: 'merchant_authorizer/tokens',
      method: 'validate',
    }).then((response) => response.payload);
  }

  async getUserInfo(): Promise<UserInfo> {
    return this.makeRequest<UserInfo>({
      service: 'merchant_authorizer/users',
      method: 'userinfo',
    }).then((response) => response.payload);
  }

  async revoke(): Promise<AuthSuccessResponse> {
    await this.makeRequest<GatewayOtpResponse>({
      method: 'revoke',
      payload: {
        clientId: this.clientId,
      },
    });
    this.session = null;
    this.username = null;
  }

  private ensureSession() {
    if (this.session === null || this.username === null) {
      logger.error('Authorization process has not been initialized');
    }
  }

  private makeRequest<T = unknown>(
    params: Partial<RequestParams> & { method: string }
  ) {
    return this.client
      .call<T>({
        host: this.host,
        version: 'v1',
        domain: 'auth',
        withLeadingDomain: true,
        service: 'merchant_authorizer/challenge_flow',
        withCredentials: true,
        ...params,
      })
      .catch((error) => {
        if (isAuthError(error)) {
          this.session = null;
          this.username = null;
        }

        throw error;
      });
  }
}
